import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { DRAWER_TABS } from "../../../../utils/constants";
import { getCompensatingControlsColumns } from "./compensatingControlsColumns";

import { useLocation } from "react-router-dom";
import { controlKeys } from "../../../../api/queryKeys";
import ControlRemediationDrawer from "../../controls/components/ControlRemediationDrawer/ControlRemediationDrawer";

const CompensatingControlsTable = ({
  selectedVulnerability,
  remediations,
  affectedEntityIdsFilter = [], // This filter allows to show only relevant entities (e.g. for a specific CVE)
}) => {
  const colors = useColors();
  const location = useLocation();
  const localSearchParams = new URLSearchParams(location.search);
  const [, setSearchParams] = useSearchParams();
  const { fetchControlsBatch, trackEvent } = useAPI();

  const controlsQuery = useQuery({
    queryKey: controlKeys.batchControls(
      selectedVulnerability?.compensating_control_ids
    ),
    queryFn: async () => {
      return await fetchControlsBatch(
        selectedVulnerability?.compensating_control_ids
      );
    },
    select: (response) => response.data,
    enabled: selectedVulnerability !== null,
  });

  const updateRemediationSearchParams = (remediation) => {
    const params = new URLSearchParams(location.search);
    params.set("remediation", encodeURIComponent(remediation.remediation_id));
    setSearchParams(params.toString());
  };

  const onRowClick = (control) => {
    const remediation = remediations.find(
      (remediation) => remediation.control_id === control.control_id
    );
    updateRemediationSearchParams(remediation);
    trackEvent(
      "User clicked on a remediation from vulnerability Drawer"
    ).then();
  };

  const removeRemediationFromSearchParams = () => {
    let searchParams = new URLSearchParams(location.search);
    searchParams.delete("remediation");
    setSearchParams(searchParams.toString());
  };

  let openRemediationDrawer = false;
  let selectedRemediation = null;
  let selectedControl = null;

  if (localSearchParams.has("remediation")) {
    const remediationId = parseInt(localSearchParams.get("remediation"));
    if (remediations) {
      selectedRemediation = remediations.find(
        (remediation) => remediation.remediation_id === remediationId
      );

      if (selectedRemediation) openRemediationDrawer = true;
    }
  }

  if (selectedRemediation && controlsQuery.data)
    selectedControl = controlsQuery.data?.find(
      (control) => control.control_id === selectedRemediation.remediation_id
    );

  const columns = getCompensatingControlsColumns(
    colors,
    affectedEntityIdsFilter
  );

  return (
    <>
      {controlsQuery.isSuccess && controlsQuery.data.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              width: "100%",
              "& .MuiDataGrid-root": {
                "& .MuiDataGrid-columnHeaders": {
                  marginLeft: "10px",
                },
                "& .MuiDataGrid-virtualScroller": {
                  "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555",
                  },
                },
              },
            }}
          >
            <StyledDataGrid
              rows={controlsQuery.data}
              columns={columns}
              pageSize={5}
              getRowId={(row) => row.control_id}
              rowsPerPageOptions={[5]}
              onRowClick={(row) => onRowClick(row?.row)}
              loading={controlsQuery.isLoading}
            />
          </Box>
          <ControlRemediationDrawer
            open={openRemediationDrawer}
            onClose={() => {
              removeRemediationFromSearchParams();
            }}
            selectedTab={DRAWER_TABS.REMEDIATION_TAB}
            selectedControl={selectedControl}
          />
        </>
      )}
    </>
  );
};

export default CompensatingControlsTable;
